import { useGetGuestSiteScheduleQuery } from './ScheduleList.graphql.generated';
import { useGetProScheduleListQuery } from './ScheduleListPro.graphql.generated';
import type { GetProScheduleListQuery } from './ScheduleListPro.graphql.generated';
import type { GetGuestSiteScheduleQuery } from './ScheduleList.graphql.generated';
import type { ScheduleListData } from './ScheduleList.types';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useIsCurrentPageSsrFetchEnabled } from '@apps/guest/shared/hooks/useIsCurrentPageSsrFetchEnabled';
import { useEventInfo } from '@shared/utils/eventInfo';
import { EventType } from '@graphql/generated-base';

const extractProData = (raw: GetProScheduleListQuery | undefined): ScheduleListData | undefined => {
  const schedule = raw?.getGuestGeneralEventScheduleForEvent;
  if (!schedule) return undefined;
  return {
    formattedSchedules: schedule.formattedSchedules,
    toBeAnnouncedItems: schedule.tbaSchedules,
    activeSession: raw?.eventByName?.activeSession ?? undefined,
  };
};

const extractWeddingData = (raw: GetGuestSiteScheduleQuery | undefined): ScheduleListData | undefined => {
  const eventByName = raw?.eventByName;
  const schedule = eventByName?.info?.schedule;
  if (!schedule) return undefined;
  return {
    formattedSchedules: schedule.formattedSchedules,
    toBeAnnouncedItems: schedule.toBeAnnouncedItems,
    activeSession: eventByName?.activeSession ?? undefined,
  };
};

export const useFetchScheduleListData = (eventHandle: string, skip?: boolean) => {
  const { isAdmin } = useEventUserRole();
  const { eventInfo, ssrEventInfo } = useEventInfo();
  const isSsrEnabled = useIsCurrentPageSsrFetchEnabled(eventHandle, 'schedule');

  const eventId = eventInfo?.eventId ?? '';
  const resolvedEventType = eventInfo?.eventType || ssrEventInfo?.eventType;
  const eventTypeResolved = resolvedEventType != null;
  const isProEvent = resolvedEventType === EventType.general;
  const usesWeddingQuery = resolvedEventType === EventType.wedding || resolvedEventType === EventType.babyRegistry;

  // Pro query — fires only for pro/general events once event type is known
  const {
    data: proData,
    previousData: proPreviousData,
    loading: proLoading,
    refetch: proRefetch,
  } = useGetProScheduleListQuery({
    variables: { eventId, eventHandle },
    batchMode: 'fast',
    ssr: isSsrEnabled,
    skip: skip || !eventTypeResolved || !isProEvent || !eventId,
  });

  // Wedding query — fires for wedding and baby registry events; skipped once we know it's a pro event
  const {
    data: weddingData,
    previousData: weddingPreviousData,
    loading: weddingLoading,
    refetch: weddingRefetch,
  } = useGetGuestSiteScheduleQuery({
    variables: { eventHandle, isAdmin },
    batchMode: 'fast',
    ssr: isSsrEnabled,
    skip: skip || (eventTypeResolved && !usesWeddingQuery),
  });

  if (isProEvent) {
    return {
      data: extractProData(proData),
      previousData: extractProData(proPreviousData),
      loading: proLoading,
      refetch: proRefetch,
    };
  } else {
    return {
      data: extractWeddingData(weddingData),
      previousData: extractWeddingData(weddingPreviousData),
      loading: weddingLoading,
      refetch: weddingRefetch,
    };
  }
};
